<template>
  <div class="form-item-container mb-6">
    <div class="fixed-content-container">
      <div class="header pb-2">
        <div class="header-title">
          <v-icon class="item-icon cursor-move" icon="mdi-drag-vertical" size="large"></v-icon>
          <div class="font-weight-black">{{ $t('i18n.gridInputTitle') }}</div>
        </div>
        <div class="header-tool-icons">
          <v-btn class="cursor-pointer" variant="text" icon @click="removeItem" :disabled="!userCanEditFormItems">
            <v-icon size="small" icon="$GoRateDeleteOutline"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.remove') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="expandGrid" :disabled="!userCanEditFormItems">
            <v-icon :icon="expandGridButtonIconName" size="large"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.collapse') }}
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </div>
    <div ref="collapsableContentContainer" class="collapsable-content-container">
      <div style="overflow: hidden;">
        <div class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language1') }}</div>
          <v-text-field v-model="itemData.question_lang1" density="compact" :placeholder="$t('i18n.questionLang1')" variant="outlined" dense hide-details="auto" clearable :rules="questionRules" counter :disabled="!userCanEditFormItems"></v-text-field>
          <v-tooltip v-if="!userCanEditFormItems" activator="parent" location="center">
            {{ $t('i18n.editNotAvailable') }}
          </v-tooltip>
        </div>
        <div v-if="bilingual" class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language2') }}</div>
          <v-text-field v-model="itemData.question_lang2" density="compact" :placeholder="$t('i18n.questionLang2')" variant="outlined" dense hide-details="auto" clearable :rules="questionRules" counter></v-text-field>
        </div>
        <v-card class="options-card-container pt-2" variant="flat">
          <div class="admissible-option-number">
            <div class="font-weight-black">
              {{ $t('i18n.options') }}
            </div>
            <div>
              <v-select v-model="numberOfOptions" :items="gridOptions" item-title="label" item-value="value" :placeholder="$t('i18n.gridOptionsNumberPlaceholder')" variant="outlined" density="compact" required :no-data-text="$t('i18n.noOptionsAvailable')"></v-select>
            </div>
          </div>
          <div class="options-list-container">
            <v-list>
              <v-list-item v-for="(option, index) in itemData.form_item_options" :key="index">
                <v-row>
                  <v-col>
                    <v-text-field v-model="option.text_lang1" counter :placeholder="$t('i18n.optionPlaceholderLang1', { index: index + 1 })" required density="compact" variant="outlined" dense hide-details="auto" clearable :rules="optionRules" :disabled="!userCanEditFormItems">
                    </v-text-field>
                    <v-tooltip v-if="!userCanEditFormItems" activator="parent" location="center">
                      {{ $t('i18n.editNotAvailable') }}
                    </v-tooltip>
                  </v-col>
                  <v-col v-if="bilingual">
                    <v-text-field v-model="option.text_lang2" counter :placeholder="$t('i18n.optionPlaceholderLang2', { index: index + 1 })" required density="compact" variant="outlined" dense hide-details="auto" clearable :rules="optionRules"></v-text-field>
                  </v-col>
                  <v-col cols=auto>
                    <v-btn variant="text" icon @click="moveUp(index)">
                      <v-icon size="x-small" icon="mdi-arrow-up"></v-icon>
                      <v-tooltip activator="parent" location="start">
                        {{ $t('i18n.moveUp') }}
                      </v-tooltip>
                    </v-btn>
                    <v-btn variant="text" icon @click="moveDown(index)">
                      <v-icon size="x-small" icon="mdi-arrow-down"></v-icon>
                      <v-tooltip activator="parent" location="start">
                        {{ $t('i18n.moveDown') }}
                      </v-tooltip>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-divider></v-divider>

              </v-list-item>
            </v-list>
            <v-alert v-if="optionCountErrorMessage && optionsStarted" class="mt-3 mb-3" variant="outlined" type="warning" dense closable>{{ optionCountErrorMessage }}
            </v-alert>
          </div>
        </v-card>
      </div>
    </div>
  </div>
  <v-divider :thickness="2" class="border-opacity-100 mb-3" color="grey"></v-divider>
</template>

<script lang="ts">
import { FormDeviceAssociation, FormItem, FormItemOption } from '@/utils/DataTypes'
import { defineComponent, PropType, ref } from 'vue'

const ADMISSIBLE_OPTION_NUMBERS = [2, 4, 6, 8]

export default defineComponent({
  name: 'GridItem',
  setup() {
    const collapsableContentContainer = ref<HTMLElement | null>(null)
    const isExpanded = ref(true)

    const expandGrid = () => {
      if (collapsableContentContainer.value) {
        isExpanded.value = !isExpanded.value
        collapsableContentContainer.value.style.gridTemplateRows = isExpanded.value ? '1fr' : '0fr'
      }
    }

    return {
      collapsableContentContainer,
      isExpanded,
      expandGrid
    }
  },
  emits: ['remove-item', 'form-item-update'],
  props: {
    formList: {
      type: Array as PropType<FormDeviceAssociation[]>,
      required: true,
      default: () => []
    },
    indexInTheSketchForm: {
      type: Number,
      required: true
    },
    bilingual: {
      type: Boolean,
      required: false,
      default: false
    },
    formItems: {
      type: Array as PropType<FormItem[]>,
      required: false,
      default: null
    },
    userCanEditFormItems: {
      type: Boolean,
      required: true,
      default: true
    }
  },
  components: {
  },
  data() {
    return {
      numberOfOptions: 0,
      gridOptions: [
        { label: '2 (1x2)', value: 2 },
        { label: '4 (2x2)', value: 4 },
        { label: '6 (2x3)', value: 6 },
        { label: '8 (2x4)', value: 8 }
      ],
      formItemOption: null as FormItemOption | null,
      itemData: {
        name: 'GRID',
        item_type_id: 6,
        question_lang1: '',
        question_lang2: '',
        required: true,
        bilingual: this.bilingual,
        form_item_options: [] as FormItemOption[]
      } as FormItem,
      optionCountErrorMessage: '',
      optionsStarted: false
    }
  },
  watch: {
    formItems: {
      handler() {
        if (this.formItems) {
          if (this.formItems[this.indexInTheSketchForm] !== undefined) {
            this.itemData = this.formItems[this.indexInTheSketchForm]

            const optionCount = this.itemData.form_item_options?.length ?? 0
            if (optionCount > 0) {
              this.numberOfOptions = optionCount
            }
          }
        }
      },
      immediate: true
    },
    bilingual: {
      handler(newBilingual) {
        this.itemData.bilingual = newBilingual
        if (!newBilingual) {
          if (this.itemData.form_item_options) {
            this.itemData.question_lang2 = ''
            this.itemData.form_item_options.forEach(option => {
              delete option.text_lang2
            })
          }
        }
      },
      immediate: true
    },
    formItemOption: {
      handler(newItemOption) {
        if (!this.itemData.form_item_options) {
          this.itemData.form_item_options = []
        }
        if (newItemOption && !this.itemData.form_item_options.includes(newItemOption)) {
          this.itemData.form_item_options.push(newItemOption)
        }
      }
    },
    numberOfOptions: {
      handler(newNumberOfOptions: number) {
        this.addOption(newNumberOfOptions)
      }
    },
    itemData: {
      handler(newItemData) {
        if (!this.itemData.form_item_options) {
          this.itemData.form_item_options = []
        }
        this.setOptionsHintMessage()
        this.$emit('form-item-update', { formItemIndex: this.indexInTheSketchForm, data: newItemData })
        if (this.optionsStarted) {
          this.setOptionsHintMessage()
        }
      },
      deep: true
    }
  },
  methods: {
    formatItemTypeTitle(input: string): string {
      const words = input.toLowerCase().split('_')
      const formatted = words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      return formatted
    },
    removeItem() {
      this.$emit('remove-item', this.indexInTheSketchForm)
    },
    addOption(numberOfOptions: number) {
      this.itemData.form_item_options = []

      while (this.itemData.form_item_options.length < numberOfOptions) {
        this.itemData.form_item_options.push({ text_lang1: '', text_lang2: '', option_order: this.itemData.form_item_options.length } as FormItemOption)
      }

      this.optionsStarted = true
      this.setOptionsHintMessage()
    },
    moveUp(index: number) {
      if (!this.itemData.form_item_options || this.itemData.form_item_options.length < 2) return

      const isFirstOption = (index === 0)
      if (isFirstOption) {
        const item = this.itemData.form_item_options.shift()
        if (item !== undefined) {
          this.itemData.form_item_options.push(item)
        }
      } else {
        const temp = this.itemData.form_item_options[index - 1]
        this.itemData.form_item_options[index - 1] = this.itemData.form_item_options[index]
        this.itemData.form_item_options[index] = temp
      }

      this.updateFormItemOptionsOrder()
    },
    moveDown(index: number) {
      if (!this.itemData.form_item_options || this.itemData.form_item_options.length < 2) return

      const isLastOption = (index === this.itemData.form_item_options.length - 1)
      if (isLastOption) {
        const item = this.itemData.form_item_options.pop()
        if (item !== undefined) {
          this.itemData.form_item_options.unshift(item)
        }
      } else {
        const temp = this.itemData.form_item_options[index + 1]
        this.itemData.form_item_options[index + 1] = this.itemData.form_item_options[index]
        this.itemData.form_item_options[index] = temp
      }

      this.updateFormItemOptionsOrder()
    },
    updateFormItemOptionsOrder() {
      if (!this.itemData.form_item_options) return

      this.itemData.form_item_options.forEach((option, index) => {
        option.option_order = index
      })
    },

    setOptionsHintMessage() {
      if (!this.itemData.form_item_options) {
        this.itemData.form_item_options = []
      }
      if (this.itemData.form_item_options.length < 2) {
        this.optionCountErrorMessage = this.$t('i18n.optionCountErrorMessage')
      } else {
        this.optionCountErrorMessage = ''
      }
    }
  },
  computed: {
    questionRules(): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.questionRequired')
        }, (value: string) => {
          if (value && value.length <= 180) return true
          return this.$t('i18n.questionMaxLength', { max: 180 })
        }, (value: string) => {
          if (value && value.length >= 3) return true
          return this.$t('i18n.questionMinLength')
        }
      ]
    },
    optionRules(): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.optionRequired')
        }, (value: string) => {
          if (value && value.length <= 180) return true
          return this.$t('i18n.optionMaxLength', { max: 180 })
        }, (value: string) => {
          if (value && value.length >= 2) return true
          return this.$t('i18n.optionMinLength')
        }
      ]
    },
    isValid(): boolean {
      if (this.itemData.form_item_options) {
        return this.optionCountErrorMessage === '' && ADMISSIBLE_OPTION_NUMBERS.includes(this.itemData.form_item_options.length)
      }
      return false
    },
    expandGridButtonIconName(): string {
      if (this.isExpanded) return 'mdi-chevron-up'
      else return 'mdi-chevron-down'
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.form-item-container {

  .fixed-content-container {
    .header {
      display: grid;
      grid-template-rows: auto;
      align-items: center;

      .header-title {
        display: grid;
        grid-template-columns: auto auto;
        justify-self: start;
        align-items: center;
      }

      .header-tool-icons {
        grid-column: 2;
        justify-self: end;
      }

    }
  }

  .collapsable-content-container {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 300ms;

    .body {
      display: grid;
      grid-template-columns: 6.5em 1fr;
      align-items: center;
    }

    .admissible-option-number {
      display: grid;
      gap: 0.5em;
    }
  }
}
</style>
